import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import ValidationGateDialog, { ValidationGateBadge, primeValidationGateCache } from '../components/ValidationGateDialog';
import KycUploadModal from '../components/KycUploadModal';
import { useAuth } from '../contexts/AuthContext';
import { backdropClick, stopBackdropPropagation } from '../lib/modalBackdrop';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  MagnifyingGlass, FilePdf, DownloadSimple, Eye, X, CheckCircle, Clock,
  PencilSimple,
  Buildings, Trash, EnvelopeSimple, PaperPlaneRight, CaretUp, CaretDown,
  Check, Warning, Paperclip, ArrowsClockwise, Funnel, Lightning, Tag, FileXls,
  ShieldCheck, Plus, Archive, Broom
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Defensive JSON reader: usa res.text() + JSON.parse per evitare
// "body stream already read" causato da estensioni del browser, service workers
// o dev tools che consumano il body Response prima di noi.
async function safeJson(res) {
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    console.warn('safeJson parse error', e);
    return {};
  }
}

export default function PreContractsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const customCteRef = useRef(null);
  const [documents, setDocuments] = useState({ system: [], custom: [] });
  const [emailModal, setEmailModal] = useState(null);
  const [unifiedModal, setUnifiedModal] = useState(null); // { group, to_email, subject, body, attachments[], cc, reply_to, testMode }
  const [quickEditModal, setQuickEditModal] = useState(null); // { group, fields }
  const [quickEditSubmitting, setQuickEditSubmitting] = useState(false);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [unifiedSending, setUnifiedSending] = useState(false);
  const [unifiedConfirm, setUnifiedConfirm] = useState(false);
  const [unifiedPreviewLoading, setUnifiedPreviewLoading] = useState(false);
  const [validationGateLead, setValidationGateLead] = useState(null); // { leadId, leadName, onProceed }
  const [kycModal, setKycModal] = useState(null); // { leadId, leadName, onAfter }
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', cte: '', condizioni: true, privacy: true });
  const [sending, setSending] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [sortKey, setSortKey] = useState('ragione_sociale');
  const [sortDir, setSortDir] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMultisito, setFilterMultisito] = useState('');
  const [filterCte, setFilterCte] = useState('');
  const [filterOfferta, setFilterOfferta] = useState('');
  const [filterRappresentante, setFilterRappresentante] = useState('');
  const [filterTipoUso, setFilterTipoUso] = useState('');
  const [filterAgenzia, setFilterAgenzia] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [bulkCte, setBulkCte] = useState('');
  const [bulkOfferta, setBulkOfferta] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, docsRes] = await Promise.all([
        fetch(`${API}/api/pre-contracts/grouped`, { credentials: 'include' }),
        fetch(`${API}/api/documents`, { credentials: 'include' }),
      ]);
      let groupsData = [];
      if (groupsRes.ok) { groupsData = await groupsRes.json(); setGroups(groupsData); }
      if (docsRes.ok) setDocuments(await docsRes.json());
      // Prime validation gate cache for all visible leads in ONE batch call
      const leadIds = groupsData.flatMap(g => g.lead_ids || [g.id]).filter(Boolean);
      if (leadIds.length > 0) {
        fetch(`${API}/api/leads/validation-gate-batch`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lead_ids: leadIds }),
        }).then(r => r.ok ? r.json() : null).then(d => {
          if (d?.results) primeValidationGateCache(d.results);
        }).catch(() => { /* graceful fallback to per-row fetch */ });
      }
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live preview: when the user edits intro_text or CTE, debounce and re-render
  // the HTML body from the backend so the anteprima + attachments update.
  useEffect(() => {
    if (!unifiedModal) return;
    const { leadId, introText, cteFilename, attachCgf, attachPrivacy, attachLetteraComparativa, extraAttachmentIds } = unifiedModal;
    const handle = setTimeout(async () => {
      setUnifiedPreviewLoading(true);
      try {
        const res = await fetch(`${API}/api/leads/${leadId}/send-unified/preview`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            intro_text: introText,
            cte_filename: cteFilename,
            attach_cgf: attachCgf,
            attach_privacy: attachPrivacy,
            attach_lettera_comparativa: attachLetteraComparativa,
            extra_attachment_ids: extraAttachmentIds || [],
          }),
        });
        if (res.ok) {
          const d = await safeJson(res);
          setUnifiedModal(m => m ? ({ ...m, body: d.body, attachments: d.attachments || [] }) : m);
        }
      } catch (_) { /* silent */ }
      finally { setUnifiedPreviewLoading(false); }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unifiedModal?.introText, unifiedModal?.cteFilename, unifiedModal?.attachCgf,
      unifiedModal?.attachPrivacy, unifiedModal?.attachLetteraComparativa,
      unifiedModal?.extraAttachmentIds?.length, unifiedModal?.leadId]);

  const cteDocuments = useMemo(() =>
    [...documents.system.filter(d => d.type === 'cte'), ...documents.custom.filter(d => d.type === 'cte')],
    [documents]
  );

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let result = groups;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(g =>
        g.ragione_sociale.toLowerCase().includes(s) ||
        g.partita_iva?.includes(s) ||
        g.pods.some(p => p.toLowerCase().includes(s))
      );
    }
    if (filterStatus === 'scartati_bo') {
      result = result.filter(g => g.is_bo_rejected);
    } else if (filterStatus) {
      result = result.filter(g => g.status === filterStatus);
    }
    if (filterMultisito === 'yes') result = result.filter(g => g.is_multisito);
    if (filterMultisito === 'no') result = result.filter(g => !g.is_multisito);
    if (filterCte === 'yes') result = result.filter(g => g.cte_document);
    if (filterCte === 'no') result = result.filter(g => !g.cte_document);
    if (filterOfferta) result = result.filter(g => g.nome_offerta === filterOfferta);
    if (filterRappresentante) {
      const r = filterRappresentante.toLowerCase();
      result = result.filter(g => (g.legale_rappresentante || '').toLowerCase().includes(r));
    }
    if (filterTipoUso) result = result.filter(g => g.tipologia_uso === filterTipoUso);
    if (filterAgenzia) result = result.filter(g => g.codice_agenzia === filterAgenzia);
    result = [...result].sort((a, b) => {
      const va = (a[sortKey] || '').toString().toLowerCase();
      const vb = (b[sortKey] || '').toString().toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return result;
  }, [groups, search, sortKey, sortDir, filterStatus, filterMultisito, filterCte, filterOfferta, filterRappresentante, filterTipoUso, filterAgenzia]);

  const offerteOptions = useMemo(() =>
    [...new Set(groups.map(g => g.nome_offerta).filter(Boolean))].sort(),
    [groups]
  );

  const agenzieOptions = useMemo(() =>
    [...new Set(groups.map(g => g.codice_agenzia).filter(Boolean))].sort(),
    [groups]
  );

  const tipoUsoOptions = useMemo(() =>
    [...new Set(groups.map(g => g.tipologia_uso).filter(Boolean))].sort(),
    [groups]
  );

  const activeFilterCount = [filterStatus, filterMultisito, filterCte, filterOfferta, filterRappresentante, filterTipoUso, filterAgenzia].filter(Boolean).length;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(g => g.id)));
  };

  const downloadWithAuth = async (url, filename) => {
    try {
      toast.info('Download in corso...');
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text();
        toast.error(text || 'Errore download');
        return;
      }
      const contentType = res.headers.get('content-type') || 'application/octet-stream';
      const buffer = await res.arrayBuffer();
      const blob = new Blob([buffer], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(blobUrl); }, 1000);
      toast.success('Download completato');
    } catch (e) { toast.error('Errore download'); }
  };

  const downloadPdf = (id, name) => downloadWithAuth(`${API}/api/leads/${id}/download-pdf`, `Contratto_${name || 'Orange'}.pdf`);
  const downloadMultisito = (id, name) => downloadWithAuth(`${API}/api/leads/${id}/download-multisito`, `Multisito_${name || 'Orange'}.pdf`);
  const downloadPackage = (id, name) => downloadWithAuth(`${API}/api/leads/${id}/download-package`, `Pacchetto_${name || 'Orange'}.pdf`);
  const downloadAll = () => downloadWithAuth(`${API}/api/leads/download-all-zip`, 'contratti_orange.zip');

  const previewContract = async (id) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}/preview-html`, { credentials: 'include' });
      if (res.ok) setPreviewHtml(await res.text());
    } catch (e) { toast.error('Errore anteprima'); }
  };

  const deletePdf = async (id) => {
    if (!window.confirm('Eliminare i PDF generati per questo cliente? Lo stato tornerà a "Lead".')) return;
    try {
      const res = await fetch(`${API}/api/leads/${id}/delete-pdf`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('PDF eliminati'); fetchData(); }
      else toast.error('Errore eliminazione');
    } catch (e) { toast.error('Errore'); }
  };

  const deletePdfBatch = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Eliminare i PDF di ${selectedIds.size} clienti selezionati? Lo stato tornerà a "Lead".`)) return;
    try {
      const res = await fetch(`${API}/api/leads/delete-pdf-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: [...selectedIds] })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`PDF eliminati per ${data.deleted} clienti`);
        setSelectedIds(new Set());
        fetchData();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore'); }
  };

  // Sprint 46 bis — Archiviazione bulk dei pre-contratti selezionati.
  const bulkArchive = async () => {
    if (!selectedIds.size) return;
    const ids = [...selectedIds];
    if (!window.confirm(
      `Archiviare ${ids.length} pre-contratti?\n\n` +
      `Lo stato passerà a "archiviato" e saranno nascosti dalla lista. ` +
      `Puoi sempre ripristinarli dall'Archivio. I clienti con contratti in attivazione o attivi non saranno toccati.`
    )) return;
    try {
      const res = await fetch(`${API}/api/leads/bulk-archive`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: ids, reason: 'bulk_precontratti' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Archiviati ${data.archived_count} pre-contratti`);
        setSelectedIds(new Set());
        fetchData();
      } else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error(`Errore: ${e.message}`); }
  };

  // Sprint 46 bis — "Ripulisci tutto": archivia TUTTI i pre-contratti non attivati.
  const cleanAllPreContracts = async () => {
    const phrase = window.prompt(
      `⚠️ Stai per ARCHIVIARE TUTTI i pre-contratti della tua azienda.\n\n` +
      `I clienti con contratti già in attivazione o attivati NON verranno toccati.\n` +
      `Operazione reversibile dall'Archivio.\n\n` +
      `Per confermare scrivi: PULISCI`
    );
    if (phrase !== 'PULISCI') {
      if (phrase !== null) toast.error('Operazione annullata: testo non corrispondente');
      return;
    }
    try {
      const res = await fetch(`${API}/api/leads/bulk-archive`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: 'pre_contracts', reason: 'cleanup_globale' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `Pulizia completata: ${data.archived_count} pre-contratti archiviati ` +
          `(${data.skipped_active} clienti attivi preservati)`,
          { duration: 7000 }
        );
        setSelectedIds(new Set());
        fetchData();
      } else toast.error(data.detail || 'Errore pulizia');
    } catch (e) { toast.error(`Errore: ${e.message}`); }
  };

  const sendForOtpSignature = async (group) => {
    const email = window.prompt(
      `Invia richiesta firma OTP a ${group.ragione_sociale}.\nEmail destinatario:`,
      group.email || ''
    );
    if (!email) return;
    if (!email.includes('@')) {
      return toast.error('Email non valida');
    }
    try {
      const res = await fetch(`${API}/api/leads/${group.id}/send-for-signature`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: email }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Email firma OTP inviata a ${email}`);
      } else {
        toast.error(data.detail || 'Errore invio');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
  };

  const openEmailModal = (group) => {
    // Legacy composer path — kept for advanced users / bulk fallback
    const leadId = (group.lead_ids && group.lead_ids[0]) || group.id;
    setValidationGateLead({
      leadId,
      leadName: group.ragione_sociale,
      onProceed: () => _actuallyOpenEmailModal(group),
    });
  };

  // === SPRINT 5: Unified send (OTP + cartaceo CTA in one email) ===
  // Loads the default template from the backend (subject + body + attachments list)
  // and opens the composer modal. User edits freely → confirm → POST send-unified.
  // NOTE (Sprint 5.2): Validation gate is now enforced at lead→pre-contract transition
  // (PDF generation), NOT at send time. So we open the composer directly.
  const openUnifiedComposer = async (group, { testMode = false, customEmail = null } = {}) => {
    const leadId = (group.lead_ids && group.lead_ids[0]) || group.id;
    setUnifiedLoading(true);
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/send-unified/preview`, {
        credentials: 'include',
      });
      const d = await safeJson(res);
      if (!res.ok) {
        toast.error(d.detail || 'Impossibile caricare anteprima email');
        return;
      }
      setUnifiedModal({
        group,
        leadId,
        to_email: customEmail !== null ? customEmail : (d.to_email || ''),
        signerEmail: d.signer_email || '',
        signerPhone: d.signer_phone || '',
        test_email_default: d.test_email_default || '',
        subject: d.subject || '',
        introText: d.intro_text || '',
        cteFilename: d.cte_filename || '',
        attachCgf: d.attach_cgf !== false,
        attachPrivacy: d.attach_privacy !== false,
        attachLetteraComparativa: d.attach_lettera_comparativa !== false,
        hasLetteraComparativa: !!d.has_lettera_comparativa,
        extraAttachments: [],
        extraAttachmentIds: [],
        ccExtra: '',
        body: d.body || '',
        attachments: d.attachments || [],
        cc: d.cc || [],
        reply_to: d.reply_to || '',
        testMode,
      });
    } catch (e) {
      toast.error('Errore di rete');
    } finally {
      setUnifiedLoading(false);
    }
  };

  const submitUnifiedSend = async (skipKyc = false) => {
    if (!unifiedModal) return;
    const { leadId, to_email, subject, introText, cteFilename, testMode,
            attachCgf, attachPrivacy, attachLetteraComparativa, extraAttachmentIds, ccExtra,
            signerEmail, signerPhone } = unifiedModal;
    if (!to_email || !to_email.includes('@')) {
      toast.error('Destinatario non valido');
      return;
    }
    setUnifiedSending(true);
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/send-unified`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email, subject,
          intro_text: introText,
          cte_filename: cteFilename,
          attach_cgf: attachCgf,
          attach_privacy: attachPrivacy,
          attach_lettera_comparativa: attachLetteraComparativa,
          extra_attachment_ids: extraAttachmentIds || [],
          cc_extra: ccExtra || '',
          signer_email: signerEmail || '',
          signer_phone: signerPhone || '',
          test_mode: testMode,
          skip_kyc_gate: skipKyc,
        }),
      });
      const d = await safeJson(res);
      if (res.ok) {
        toast.success(
          testMode
            ? `Email TEST inviata a ${d.sent_to}`
            : `Inviato a ${d.sent_to}${d.cc?.length ? ` (CC ${d.cc[0]})` : ''}`,
          { duration: 6000 }
        );
        setUnifiedModal(null);
        setUnifiedConfirm(false);
        fetchData();
      } else if (res.status === 409 && typeof d.detail === 'string' && d.detail.toLowerCase().includes('kyc')) {
        // Sprint 46 - KYC gate vincolante: apre il modal di upload del documento.
        // Gli admin possono comunque procedere senza documento (bypass tracciato).
        toast.error(d.detail, {
          duration: 8000,
          action: {
            label: 'Carica documento',
            onClick: () => setKycModal({
              leadId,
              leadName: unifiedModal.group?.ragione_sociale,
              onAfter: () => submitUnifiedSend(),
            }),
          },
        });
        // Pre-apri il modal: l'utente si aspetta una azione immediata.
        setKycModal({
          leadId,
          leadName: unifiedModal.group?.ragione_sociale,
          onAfter: () => submitUnifiedSend(),
          allowAdminSkip: isAdmin,
          onSkip: () => { setKycModal(null); submitUnifiedSend(true); },
        });
      } else {
        // Structured SMTP error (Sprint 5.3)
        if (d.detail?.code === 'smtp_auth_failed') {
          toast.error(d.detail.message, {
            duration: 10000,
            action: {
              label: 'Apri Impostazioni',
              onClick: () => { window.location.href = '/settings'; },
            },
          });
        } else if (d.detail?.code === 'smtp_connection_failed') {
          toast.error(d.detail.message, { duration: 8000 });
        } else if (d.detail?.message) {
          toast.error(d.detail.message, { duration: 7000 });
        } else {
          toast.error(typeof d.detail === 'string' ? d.detail : 'Errore invio', { duration: 7000 });
        }
      }
    } catch (e) {
      toast.error(`Errore di rete: ${e.message || e}`, { duration: 7000 });
    } finally {
      setUnifiedSending(false);
    }
  };

  // Definitive send — composer opens directly (no pre-send gate dialog)
  const sendUnified = (group) => {
    openUnifiedComposer(group, { testMode: false });
  };

  // Sprint 43 - Quick Edit pre-firma
  const openQuickEdit = (group) => {
    const leadId = (group.lead_ids && group.lead_ids[0]) || group.id;
    setQuickEditModal({
      group, leadId,
      fields: {
        consenso_marketing: (group.consenso_marketing || '').toString().toUpperCase() === 'SI',
        consenso_terzi: (group.consenso_terzi || '').toString().toUpperCase() === 'SI',
        consenso_invio_documenti: (group.consenso_invio_documenti || '').toString().toUpperCase() === 'SI',
        email: group.email || '',
        telefono: group.telefono || '',
        indirizzo_recapito: group.indirizzo_recapito || '',
        comune_recapito: group.comune_recapito || '',
        provincia_recapito: group.provincia_recapito || '',
        cap_recapito: group.cap_recapito || '',
        metodo_pagamento: group.metodo_pagamento || '',
        iban: group.iban || '',
        note_interne: group.note_interne || '',
      },
    });
  };

  const submitQuickEdit = async () => {
    if (!quickEditModal) return;
    setQuickEditSubmitting(true);
    try {
      const payload = {
        consenso_marketing: quickEditModal.fields.consenso_marketing ? 'SI' : 'NO',
        consenso_terzi: quickEditModal.fields.consenso_terzi ? 'SI' : 'NO',
        consenso_invio_documenti: quickEditModal.fields.consenso_invio_documenti ? 'SI' : 'NO',
        email: quickEditModal.fields.email,
        telefono: quickEditModal.fields.telefono,
        indirizzo_recapito: quickEditModal.fields.indirizzo_recapito,
        comune_recapito: quickEditModal.fields.comune_recapito,
        provincia_recapito: quickEditModal.fields.provincia_recapito,
        cap_recapito: quickEditModal.fields.cap_recapito,
        metodo_pagamento: quickEditModal.fields.metodo_pagamento,
        iban: quickEditModal.fields.iban,
        note_interne: quickEditModal.fields.note_interne,
      };
      const res = await fetch(`${API}/api/leads/${quickEditModal.leadId}/quick-edit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await safeJson(res);
      if (res.ok) {
        const inv = d.signature_requests_invalidated || 0;
        toast.success(
          `Modifiche salvate, PDF rigenerato${inv > 0 ? ` (${inv} firma pendente invalidata — reinviare al cliente)` : ''}`,
          { duration: 6000 },
        );
        setQuickEditModal(null);
        fetchData();
      } else {
        toast.error(d.detail || 'Errore modifica veloce');
      }
    } catch (e) {
      toast.error('Errore di rete');
    } finally {
      setQuickEditSubmitting(false);
    }
  };

  // TEST send — composer opens directly with destinatario pre-filled with the
  // logged-in user's email so it's fast to verify; the user can change it.
  const sendUnifiedTest = async (group) => {
    await openUnifiedComposer(group, { testMode: true, customEmail: '' });
  };

  const _actuallyOpenEmailModal = (group) => {
    setEmailForm({
      subject: `Benvenuto in Orange Energy - Documentazione contrattuale - ${group.ragione_sociale}`,      body: `<p>Gentile/Spett.le <strong>${group.ragione_sociale}</strong>,</p>

<p>è un piacere per noi darTi il benvenuto tra i clienti di <strong>Orange Energy</strong>.</p>

<p>Come anticipato dal Tuo referente commerciale di fiducia, la nostra struttura è stata
selezionata per garantirti la massima continuità nella fornitura di energia elettrica e gas,
offrendoti un servizio basato su <em>solidità</em>, <em>trasparenza</em> e <em>vicinanza al territorio</em>.</p>

<p>Il nostro obiettivo è rendere questo passaggio semplice e vantaggioso. In accordo con
le Tue esigenze analizzate dal Tuo consulente, abbiamo predisposto per Te una proposta
contrattuale che riflette la continuità delle Tue attuali condizioni operative,
con tutta la forza di un gruppo orientato all'innovazione.</p>

<p><strong>Cosa troverai in allegato</strong> &mdash; la documentazione necessaria per
attivare ufficialmente il Tuo nuovo profilo di fornitura:</p>

<ul style="line-height:1.7;">
  <li><strong>Modulo di adesione / Contratto</strong>${group.is_multisito ? ' + <strong>Allegato Multisito</strong>' : ''}: da sottoscrivere per confermare la Tua fiducia.</li>
  <li><strong>Condizioni Tecnico-Economiche (CTE)</strong>: il dettaglio dei prezzi a Te
      riservati in linea con le tue attuali condizioni e con applicazione di una piccola
      scontistica a te dedicata.</li>
  <li><strong>Condizioni Generali di Fornitura (CGF)</strong>: il set informativo
      trasparente sulle modalità del servizio.</li>
  <li><strong>Informativa Privacy</strong>.</li>
</ul>

<p>Nulla cambierà nel Tuo rapporto con il consulente che Ti segue, che rimarrà il Tuo
punto di riferimento principale.</p>

<p><strong>Prossimi passi</strong> &mdash; Ti invitiamo a prendere visione dei documenti
e a procedere con la sottoscrizione:</p>

<ol style="line-height:1.7;">
  <li>Stampa, firma e scansiona il contratto e le condizioni economiche, oppure</li>
  <li><a href="#" style="color:#ff6a00;font-weight:bold;text-decoration:none;">Clicca qui per la firma OTP</a> &mdash; risparmia tempo e aiuta l'ambiente.</li>
</ol>

<p>Restiamo a Tua completa disposizione per ogni necessità e Ti ringraziamo per averci scelto.</p>`,
      cte: group.cte_document || '',
      condizioni: true,
      privacy: true,
    });
    setEmailModal(group);
  };

  const openBatchEmail = async () => {
    const count = selectedIds.size;
    if (!count) { toast.error('Seleziona almeno un cliente'); return; }

    // Preflight: validation gate batch
    try {
      const ids = Array.from(selectedIds).flatMap(groupId => {
        const g = groups.find(x => x.id === groupId);
        return g ? (g.lead_ids || [g.id]) : [];
      });
      const r = await fetch(`${API}/api/leads/validation-gate-batch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: ids }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.summary.cannot_send > 0) {
          const proceed = window.confirm(
            `⚠️ Attenzione: ${d.summary.cannot_send} su ${d.summary.total} clienti NON possono ricevere l'email (dati mancanti).\n` +
            `Solo ${d.summary.can_send} verranno inviati. Procedere?`
          );
          if (!proceed) return;
        } else if (d.summary.with_warnings > 0) {
          toast.info(`${d.summary.with_warnings} clienti hanno avvisi non bloccanti`);
        }
      }
    } catch (e) { /* graceful: continue anyway */ }

    setEmailForm({
      subject: 'Benvenuto in Orange Energy - Documentazione contrattuale',
      body: `<p>Gentile/Spett.le Cliente,</p>

<p>è un piacere per noi darTi il benvenuto tra i clienti di <strong>Orange Energy</strong>.</p>

<p>Come anticipato dal Tuo referente commerciale di fiducia, la nostra struttura è stata
selezionata per garantirti la massima continuità nella fornitura di energia elettrica e gas,
offrendoti un servizio basato su <em>solidità</em>, <em>trasparenza</em> e <em>vicinanza al territorio</em>.</p>

<p>Il nostro obiettivo è rendere questo passaggio semplice e vantaggioso. In accordo con
le Tue esigenze analizzate dal Tuo consulente, abbiamo predisposto per Te una proposta
contrattuale che riflette la continuità delle Tue attuali condizioni operative,
con tutta la forza di un gruppo orientato all'innovazione.</p>

<p><strong>Cosa troverai in allegato</strong> &mdash; la documentazione necessaria per
attivare ufficialmente il Tuo nuovo profilo di fornitura:</p>

<ul style="line-height:1.7;">
  <li><strong>Modulo di adesione / Contratto</strong>: da sottoscrivere per confermare la Tua fiducia.</li>
  <li><strong>Condizioni Tecnico-Economiche (CTE)</strong>: il dettaglio dei prezzi a Te riservati.</li>
  <li><strong>Condizioni Generali di Fornitura (CGF)</strong>.</li>
  <li><strong>Informativa Privacy</strong>.</li>
</ul>

<p>Nulla cambierà nel Tuo rapporto con il consulente che Ti segue, che rimarrà il Tuo
punto di riferimento principale.</p>

<p><strong>Prossimi passi</strong> &mdash; Ti invitiamo a prendere visione dei documenti
e a procedere con la sottoscrizione:</p>

<ol style="line-height:1.7;">
  <li>Stampa, firma e scansiona il contratto e le condizioni economiche, oppure</li>
  <li><a href="#" style="color:#ff6a00;font-weight:bold;text-decoration:none;">Clicca qui per la firma OTP</a> &mdash; risparmia tempo e aiuta l'ambiente.</li>
</ol>

<p>Restiamo a Tua completa disposizione per ogni necessità e Ti ringraziamo per averci scelto.</p>`,
      cte: '',
      condizioni: true,
      privacy: true,
    });
    setEmailModal({ batch: true, count });
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      if (emailModal.batch) {
        const lead_ids = [...selectedIds];
        const res = await fetch(`${API}/api/pre-contracts/send-email-batch`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_ids, subject: emailForm.subject, body: emailForm.body,
            attach_cte_auto: true, attach_condizioni: emailForm.condizioni, attach_privacy: emailForm.privacy,
          })
        });
        const data = await res.json();
        if (res.ok) {
          toast.success(`${data.sent} email inviate${data.failed > 0 ? `, ${data.failed} fallite` : ''}`);
          if (data.errors?.length) data.errors.forEach(e => toast.error(e));
          setEmailModal(null);
          setSelectedIds(new Set());
          fetchData();
        } else toast.error(data.detail);
      } else {
        const res = await fetch(`${API}/api/pre-contracts/send-email`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lead_id: emailModal.id, to_email: emailModal.email,
            subject: emailForm.subject, body: emailForm.body,
            attach_cte: emailForm.cte, attach_condizioni: emailForm.condizioni, attach_privacy: emailForm.privacy,
          })
        });
        const data = await res.json();
        if (res.ok) {
          toast.success(data.message);
          setEmailModal(null);
          fetchData();
        } else toast.error(data.detail);
      }
    } catch (e) { toast.error('Errore invio email'); }
    finally { setSending(false); }
  };

  const loadHistory = async (ragione) => {
    try {
      const res = await fetch(`${API}/api/pre-contracts/${encodeURIComponent(ragione)}/email-history`, { credentials: 'include' });
      if (res.ok) {
        setEmailHistory(await res.json());
        setHistoryModal(ragione);
      }
    } catch (e) { toast.error('Errore caricamento storico'); }
  };

  const updateCte = async (leadId, cteDoc) => {
    // Optimistic update
    setGroups(prev => prev.map(g => g.id === leadId ? { ...g, cte_document: cteDoc } : g));
    try {
      const res = await fetch(`${API}/api/pre-contracts/${leadId}/cte`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cte_document: cteDoc })
      });
      if (res.ok) {
        toast.success(cteDoc ? 'CTE assegnata' : 'CTE rimossa');
      } else {
        toast.error('Errore aggiornamento CTE');
        fetchData(); // revert on error
      }
    } catch (e) { toast.error('Errore aggiornamento CTE'); fetchData(); }
  };

  const bulkAssignCte = async () => {
    if (!selectedIds.size) { toast.error('Seleziona almeno un cliente'); return; }
    try {
      const res = await fetch(`${API}/api/pre-contracts/bulk-assign-cte`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: [...selectedIds], cte_document: bulkCte })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`CTE ${bulkCte ? 'assegnata' : 'rimossa'} a ${data.modified} clienti`);
        setBulkCte('');
        fetchData();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore assegnazione CTE'); }
  };

  const bulkAssignOfferta = async () => {
    if (!selectedIds.size) { toast.error('Seleziona almeno un cliente'); return; }
    try {
      const res = await fetch(`${API}/api/pre-contracts/bulk-assign-offerta`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: [...selectedIds], nome_offerta: bulkOfferta })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Offerta ${bulkOfferta ? `"${bulkOfferta}"` : 'rimossa'} a ${data.modified} clienti`);
        setBulkOfferta('');
        fetchData();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore assegnazione offerta'); }
  };

  const autoAssignCte = async () => {
    try {
      const res = await fetch(`${API}/api/pre-contracts/auto-assign-cte`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Auto-assegnate ${data.assigned} CTE su ${data.total} clienti`);
        fetchData();
      } else toast.error(data.detail);
    } catch (e) { toast.error('Errore auto-assegnazione'); }
  };

  const StatusBadge = ({ group }) => {
    // Scartati BO takes precedence over any other status
    if (group.is_bo_rejected) {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200"
              data-testid={`status-${group.id}`}
              title={group.bo_rejected_motivo || 'Scartato dal back-office'}>
          <Warning size={10} weight="fill" /> Scartato BO
        </span>
      );
    }
    // Use extended_status if present, fall back to legacy status
    const st = group.extended_status || group.status;
    const map = {
      da_inviare: { bg: 'bg-amber-50 text-amber-700', icon: Clock, label: 'Da Inviare' },
      inviato_mail_test: { bg: 'bg-amber-50 text-amber-700', icon: PaperPlaneRight, label: 'Mail Test' },
      inviato_mail_otp: { bg: 'bg-blue-50 text-blue-700', icon: PaperPlaneRight, label: 'Mail OTP' },
      scaricato_cartaceo: { bg: 'bg-purple-50 text-purple-700', icon: DownloadSimple, label: 'Scaricato' },
      in_attesa_firma: { bg: 'bg-orange-50 text-orange-700', icon: Clock, label: 'Attesa Firma' },
      firmato_otp: { bg: 'bg-green-50 text-green-700', icon: CheckCircle, label: 'Firmato OTP' },
      firmato_cartaceo: { bg: 'bg-green-50 text-green-700', icon: CheckCircle, label: 'Firmato Cart.' },
      rifiutato: { bg: 'bg-red-50 text-red-700', icon: X, label: 'Rifiutato' },
      scaduto: { bg: 'bg-slate-100 text-slate-500', icon: Clock, label: 'Scaduto' },
      // legacy
      aperto: { bg: 'bg-blue-50 text-blue-700', icon: Eye, label: 'Aperto' },
      inviato: { bg: 'bg-green-50 text-green-700', icon: CheckCircle, label: 'Inviato' },
      da_firmare: { bg: 'bg-amber-50 text-amber-700', icon: Clock, label: 'Da inviare' },
    };
    const cfg = map[st] || map.da_firmare;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.bg}`}
            data-testid={`status-${group.id}`}>
        <Icon size={10} /> {cfg.label}
      </span>
    );
  };

  const EmailInfoCell = ({ group, onOpen }) => {
    if (!group.last_email_date) {
      return <span className="text-[10px] text-slate-300">—</span>;
    }
    const d = new Date(group.last_email_date);
    const fmt = d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    return (
      <button onClick={(e) => { e.stopPropagation(); onOpen(group); }}
              data-testid={`email-info-${group.id}`}
              className="text-left text-[10px] leading-tight hover:bg-orange-50 rounded px-1 py-0.5 transition">
        <div className="font-mono text-slate-700 truncate max-w-[130px]">{group.last_email_to || '—'}</div>
        <div className="text-slate-400 flex items-center gap-1">
          {fmt}
          {group.last_email_opened && <Eye size={9} className="text-blue-600" weight="bold" />}
        </div>
      </button>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortKey !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="pre-contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="pre-contracts-title">Pre-contratti</h1>
          <p className="text-sm text-slate-500">
            {filtered.length}/{groups.length} clienti
            {selectedIds.size > 0 && ` · ${selectedIds.size} selezionati`}
            {groups.filter(g => !g.cte_document).length > 0 && (
              <span className="text-amber-600 ml-2">{groups.filter(g => !g.cte_document).length} senza CTE</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={autoAssignCte}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-md text-xs font-medium transition-colors"
            data-testid="auto-assign-cte" title="Assegna CTE automaticamente in base all'offerta">
            <Lightning size={14} /> Auto CTE
          </button>
          {groups.length > 0 && (
            <button onClick={downloadAll}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium transition-colors"
              data-testid="download-all-zip">
              <DownloadSimple size={14} /> Scarica ZIP
            </button>
          )}
          {groups.length > 0 && (
            <button onClick={cleanAllPreContracts}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-md text-xs font-medium transition-colors"
              title="Archivia tutti i pre-contratti non attivati (cleanup)"
              data-testid="clean-all-precontracts">
              <Broom size={14} /> Ripulisci tutto
            </button>
          )}
        </div>
      </div>

      {/* Quick status tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-0.5" data-testid="status-tabs">
        {[
          { value: '', label: 'Tutti', count: groups.length, cls: 'slate' },
          { value: 'da_firmare', label: 'Da inviare', count: groups.filter(g => !g.is_bo_rejected && (g.status === 'da_firmare' || g.extended_status === 'da_inviare')).length, cls: 'amber' },
          { value: 'inviato', label: 'Inviati', count: groups.filter(g => !g.is_bo_rejected && (g.status === 'inviato' || g.extended_status?.startsWith('inviato'))).length, cls: 'blue' },
          { value: 'aperto', label: 'Aperti', count: groups.filter(g => !g.is_bo_rejected && g.status === 'aperto').length, cls: 'indigo' },
          { value: 'scartati_bo', label: '⚠ Scartati BO', count: groups.filter(g => g.is_bo_rejected).length, cls: 'red' },
        ].map(tab => {
          const active = filterStatus === tab.value;
          const base = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition';
          const activeCls = tab.cls === 'red'
            ? 'border-red-500 text-red-700 bg-red-50'
            : tab.cls === 'amber'
            ? 'border-amber-500 text-amber-700 bg-amber-50'
            : tab.cls === 'blue'
            ? 'border-blue-500 text-blue-700 bg-blue-50'
            : tab.cls === 'indigo'
            ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
            : 'border-slate-700 text-slate-900 bg-slate-100';
          const idleCls = 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50';
          return (
            <button key={tab.value || 'all'} onClick={() => setFilterStatus(tab.value)}
                    className={`${base} ${active ? activeCls : idleCls}`}
                    data-testid={`status-tab-${tab.value || 'all'}`}>
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? 'bg-white/60' : 'bg-slate-200 text-slate-600'}`}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Funnel size={14} className="text-slate-400 shrink-0" />
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca nome, P.IVA, POD..."
              className="pl-8 h-8 text-xs" data-testid="pre-contracts-search" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="text-xs border rounded px-2 py-1.5 bg-white h-8" data-testid="filter-status">
            <option value="">Tutti gli stati</option>
            <option value="da_firmare">Da inviare</option>
            <option value="inviato">Inviato</option>
            <option value="aperto">Aperto</option>
            <option value="scartati_bo">⚠ Scartati BO</option>
          </select>
          <select value={filterMultisito} onChange={e => setFilterMultisito(e.target.value)}
            className="text-xs border rounded px-2 py-1.5 bg-white h-8" data-testid="filter-multisito">
            <option value="">Tutti i tipi</option>
            <option value="yes">Multisito</option>
            <option value="no">Singolo POD</option>
          </select>
          <select value={filterCte} onChange={e => setFilterCte(e.target.value)}
            className="text-xs border rounded px-2 py-1.5 bg-white h-8" data-testid="filter-cte">
            <option value="">CTE: tutte</option>
            <option value="yes">CTE assegnata</option>
            <option value="no">Senza CTE</option>
          </select>
          <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs border rounded h-8 transition-colors ${showAdvancedFilters || activeFilterCount > 0 ? 'bg-orange-50 border-orange-300 text-orange-700' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
            data-testid="toggle-advanced-filters">
            <Funnel size={12} weight={showAdvancedFilters ? 'fill' : 'regular'} />
            Filtri{activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
          {(filterStatus || filterMultisito || filterCte || filterOfferta || search || filterRappresentante || filterTipoUso || filterAgenzia) && (
            <button onClick={() => { setFilterStatus(''); setFilterMultisito(''); setFilterCte(''); setFilterOfferta(''); setSearch(''); setFilterRappresentante(''); setFilterTipoUso(''); setFilterAgenzia(''); }}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1" data-testid="clear-filters">
              <X size={12} className="inline mr-0.5" /> Resetta
            </button>
          )}
        </div>

        {/* Advanced filters panel */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-2 border-t border-slate-200" data-testid="advanced-filters-panel">
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Legale Rappresentante</label>
              <Input value={filterRappresentante} onChange={e => setFilterRappresentante(e.target.value)}
                placeholder="Cerca..." className="text-xs h-7" data-testid="filter-rappresentante" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Tipologia Uso</label>
              <select value={filterTipoUso} onChange={e => setFilterTipoUso(e.target.value)}
                className="w-full text-xs border rounded px-2 py-1 bg-white" data-testid="filter-tipo-uso">
                <option value="">Tutte</option>
                {tipoUsoOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Agenzia</label>
              <select value={filterAgenzia} onChange={e => setFilterAgenzia(e.target.value)}
                className="w-full text-xs border rounded px-2 py-1 bg-white" data-testid="filter-agenzia">
                <option value="">Tutte</option>
                {agenzieOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid="bulk-actions-bar">
          <span className="text-xs font-medium text-orange-800">{selectedIds.size} selezionati:</span>

          {/* CTE Assignment */}
          <div className="flex items-center gap-1.5">
            <select value={bulkCte} onChange={e => setBulkCte(e.target.value)}
              className="text-xs border border-orange-200 rounded px-2 py-1 bg-white" data-testid="bulk-cte-select">
              <option value="">CTE...</option>
              {cteDocuments.map(d => <option key={d.id} value={d.filename}>{d.label}</option>)}
            </select>
            <button onClick={bulkAssignCte}
              className="px-2.5 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded font-medium disabled:opacity-50"
              data-testid="bulk-assign-cte-btn">
              <Paperclip size={12} className="inline mr-0.5" /> Assegna CTE
            </button>
          </div>

          <div className="w-px h-5 bg-orange-200" />

          {/* Email actions */}
          <button onClick={openBatchEmail}
            className="px-2.5 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded font-medium"
            data-testid="send-batch-email">
            <PaperPlaneRight size={12} className="inline mr-0.5" /> Invia Email
          </button>

          <div className="w-px h-5 bg-orange-200" />

          <button onClick={deletePdfBatch}
            className="px-2.5 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded font-medium"
            data-testid="delete-pdf-batch">
            <Trash size={12} className="inline mr-0.5" /> Elimina PDF
          </button>

          <button onClick={bulkArchive}
            className="px-2.5 py-1 text-xs bg-slate-700 hover:bg-slate-800 text-white rounded font-medium"
            data-testid="bulk-archive-btn">
            <Archive size={12} className="inline mr-0.5" /> Archivia selezionati
          </button>

          <button onClick={() => setSelectedIds(new Set())}
            className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-white rounded"
            data-testid="deselect-all">
            <X size={12} className="inline mr-0.5" /> Deseleziona
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-auto bg-white" style={{ maxHeight: '70vh' }}>
        <Table>
          <TableHeader className="sticky top-0 bg-white z-10">
            <TableRow className="border-b-2">
              <TableHead className="w-10">
                <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0}
                  onChange={toggleAll} className="accent-orange-500" data-testid="select-all-pc" />
              </TableHead>
              <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('ragione_sociale')}>
                Ragione Sociale <SortIcon field="ragione_sociale" />
              </TableHead>
              <TableHead className="text-xs">P.IVA</TableHead>
              <TableHead className="text-xs">POD</TableHead>
              <TableHead className="text-xs">CTE</TableHead>
              <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('pdf_generated_at')}>
                Generato <SortIcon field="pdf_generated_at" />
              </TableHead>
              <TableHead className="text-xs">Ultima Email</TableHead>
              <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('status')}>
                Stato <SortIcon field="status" />
              </TableHead>
              <TableHead className="text-xs text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(group => (
              <TableRow key={group.id} className={`text-xs hover:bg-slate-50 ${selectedIds.has(group.id) ? 'bg-orange-50' : ''}`}>
                <TableCell>
                  <input type="checkbox" checked={selectedIds.has(group.id)} onChange={() => toggleSelect(group.id)}
                    className="accent-orange-500" data-testid={`select-pc-${group.id}`} />
                </TableCell>
                <TableCell className="font-medium text-slate-900 max-w-[200px]">
                  <div className="flex items-center gap-2">
                    <ValidationGateBadge leadId={(group.lead_ids && group.lead_ids[0]) || group.id} />
                    <div>{group.ragione_sociale}</div>
                  </div>
                  {group.email && <div className="text-[10px] text-slate-400 truncate">{group.email}</div>}
                  <div className="mt-0.5 flex items-center gap-1">
                    {group.kyc_ok ? (
                      <>
                        <button
                          onClick={() => window.open(`${API}/api/leads/${group.id}/identity-doc/file`, '_blank')}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold hover:bg-emerald-100 transition-colors"
                          title={`Documento d'identità ${group.identity_doc_type || ''} — clicca per visualizzare`}
                          data-testid={`kyc-view-${group.id}`}>
                          <ShieldCheck size={10} weight="fill" /> KYC {group.identity_doc_type || 'OK'}
                        </button>
                        <button
                          onClick={() => setKycModal({ leadId: group.id, leadName: group.ragione_sociale, onAfter: fetchData })}
                          className="text-[10px] text-slate-400 hover:text-orange-600 underline"
                          title="Sostituisci documento d'identità"
                          data-testid={`kyc-replace-${group.id}`}>
                          sostituisci
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setKycModal({ leadId: group.id, leadName: group.ragione_sociale, onAfter: fetchData })}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold hover:bg-amber-100 transition-colors"
                        title="Documento d'identità mancante — clicca per caricarlo"
                        data-testid={`kyc-upload-${group.id}`}>
                        <ShieldCheck size={10} /> KYC mancante
                      </button>
                    )}
                  </div>
                  {group.is_bo_rejected && (
                    <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold"
                         title={`Respinto in Back Office${group.bo_rejected_at ? ' il ' + new Date(group.bo_rejected_at).toLocaleDateString('it-IT') : ''}: ${group.bo_rejected_motivo || '(motivo non specificato)'}`}
                         data-testid={`bo-rejected-badge-${group.id}`}>
                      <Warning size={10} weight="fill" /> Scartato BO
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-slate-600 font-mono text-[10px]">{group.partita_iva}</TableCell>
                <TableCell>
                  {group.is_multisito ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-medium">
                      <Buildings size={10} /> {group.pod_count} siti
                    </span>
                  ) : (
                    <span className="font-mono text-slate-600 text-[10px]">{group.pods[0]}</span>
                  )}
                </TableCell>
                <TableCell>
                  <select value={group.cte_document || ''} onChange={e => updateCte(group.id, e.target.value)}
                    className="text-[10px] border rounded px-1 py-0.5 bg-white max-w-[100px]"
                    data-testid={`cte-select-${group.id}`}>
                    <option value="">-- Nessuna --</option>
                    {cteDocuments.map(d => <option key={d.id} value={d.filename}>{d.label}</option>)}
                  </select>
                </TableCell>
                <TableCell className="text-slate-500">
                  {group.pdf_generated_at ? new Date(group.pdf_generated_at).toLocaleDateString('it-IT') : '--'}
                </TableCell>
                <TableCell>
                  <EmailInfoCell group={group} onOpen={() => loadHistory(group.ragione_sociale)} />
                </TableCell>
                <TableCell><StatusBadge group={group} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-0.5 justify-end">
                    <button onClick={() => sendUnified(group)}
                      disabled={unifiedLoading}
                      className="p-1.5 rounded hover:bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors disabled:opacity-50"
                      title="Invia al cliente (OTP + Cartaceo)" data-testid={`send-unified-${group.id}`}>
                      <PaperPlaneRight size={14} weight="fill" />
                    </button>
                    <button onClick={() => sendUnifiedTest(group)}
                      disabled={unifiedLoading}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-50"
                      title="Invia Test a indirizzo alternativo (include OTP + cartaceo)" data-testid={`send-test-${group.id}`}>
                      <span className="text-[10px] font-bold">TEST</span>
                    </button>
                    <button onClick={() => previewContract(group.id)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                      title="Anteprima Contratto" data-testid={`preview-pc-${group.id}`}>
                      <Eye size={14} />
                    </button>
                    <button onClick={() => openQuickEdit(group)}
                      className="p-1.5 rounded hover:bg-purple-50 text-purple-600 transition-colors"
                      title="Modifica veloce (consensi, contatti, recapito) — solo pre-firma"
                      data-testid={`quick-edit-${group.id}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => downloadPackage(group.id, group.ragione_sociale)}
                      className="p-1.5 rounded hover:bg-amber-50 text-amber-600 transition-colors"
                      title="Pacchetto Completo (Contratto + CTE + Condizioni + Privacy)"
                      data-testid={`download-pkg-${group.id}`}>
                      <DownloadSimple size={14} />
                    </button>
                    <button onClick={() => downloadPdf(group.id, group.ragione_sociale)}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600 transition-colors"
                      title="Solo Contratto" data-testid={`download-pc-${group.id}`}>
                      <FilePdf size={14} />
                    </button>
                    {group.is_multisito && (
                      <button onClick={() => downloadMultisito(group.id, group.ragione_sociale)}
                        className="p-1.5 rounded hover:bg-purple-50 text-purple-600 transition-colors"
                        title="Allegato Multisito" data-testid={`download-multi-${group.id}`}>
                        <Buildings size={14} />
                      </button>
                    )}
                    {group.email_count > 0 && (
                      <button onClick={() => loadHistory(group.ragione_sociale)}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-500 transition-colors relative"
                        title="Storico Email" data-testid={`history-${group.id}`}>
                        <ArrowsClockwise size={14} />
                        <span className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full text-[8px] w-3.5 h-3.5 flex items-center justify-center">
                          {group.email_count}
                        </span>
                      </button>
                    )}
                    <button onClick={() => deletePdf(group.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      title="Elimina PDF" data-testid={`delete-pdf-${group.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                  Nessun pre-contratto generato. Vai su Leads per generare i PDF.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Unified Send Composer Modal (Sprint 5) — OTP + Cartaceo CTAs */}
      {quickEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             data-testid="quick-edit-modal"
             onClick={e => { if (e.target === e.currentTarget && !quickEditSubmitting) setQuickEditModal(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <PencilSimple size={22} className="text-purple-600" /> Modifica veloce pre-firma
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                <strong>{quickEditModal.group?.ragione_sociale}</strong> — modifiche ai consensi, contatti, indirizzo recapito.
                Il PDF verrà rigenerato e le eventuali richieste firma già inviate saranno invalidate (l'agente dovrà reinviare).
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Consensi */}
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                <div className="text-sm font-semibold text-amber-900 mb-2">🔒 Consensi GDPR</div>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1"
                           checked={quickEditModal.fields.consenso_marketing}
                           onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, consenso_marketing: e.target.checked } }))}
                           data-testid="qe-consenso-marketing" />
                    <span><strong>Marketing</strong> — comunicazioni commerciali e promozionali</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1"
                           checked={quickEditModal.fields.consenso_terzi}
                           onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, consenso_terzi: e.target.checked } }))}
                           data-testid="qe-consenso-terzi" />
                    <span><strong>Comunicazione a terzi</strong> — possibilità di rivendita dei dati</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="mt-1"
                           checked={quickEditModal.fields.consenso_invio_documenti}
                           onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, consenso_invio_documenti: e.target.checked } }))}
                           data-testid="qe-consenso-invio-documenti" />
                    <span><strong>Invio documenti contrattuali</strong> via email/PEC</span>
                  </label>
                </div>
              </div>

              {/* Contatti */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700">Email cliente</label>
                  <input type="email" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                         value={quickEditModal.fields.email}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, email: e.target.value } }))}
                         data-testid="qe-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Telefono</label>
                  <input type="tel" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                         value={quickEditModal.fields.telefono}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, telefono: e.target.value } }))}
                         data-testid="qe-telefono" />
                </div>
              </div>

              {/* Indirizzo recapito */}
              <div className="border-t pt-3">
                <div className="text-xs font-medium text-slate-700 mb-2">📮 Indirizzo recapito (se diverso da fornitura)</div>
                <div className="grid grid-cols-12 gap-2">
                  <input type="text" placeholder="Via e civico" className="col-span-7 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                         value={quickEditModal.fields.indirizzo_recapito}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, indirizzo_recapito: e.target.value } }))}
                         data-testid="qe-indirizzo-recapito" />
                  <input type="text" placeholder="Comune" className="col-span-3 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                         value={quickEditModal.fields.comune_recapito}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, comune_recapito: e.target.value } }))} />
                  <input type="text" placeholder="PR" maxLength={2} className="col-span-1 px-2 py-2 border border-slate-300 rounded-lg text-sm uppercase"
                         value={quickEditModal.fields.provincia_recapito}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, provincia_recapito: e.target.value.toUpperCase() } }))} />
                  <input type="text" placeholder="CAP" maxLength={5} className="col-span-1 px-2 py-2 border border-slate-300 rounded-lg text-sm"
                         value={quickEditModal.fields.cap_recapito}
                         onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, cap_recapito: e.target.value } }))} />
                </div>
              </div>

              {/* Pagamento + note */}
              <div className="border-t pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Metodo pagamento</label>
                    <select className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            value={quickEditModal.fields.metodo_pagamento}
                            onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, metodo_pagamento: e.target.value } }))}
                            data-testid="qe-metodo-pagamento">
                      <option value="">— Seleziona —</option>
                      <option value="SDD">SDD (Domiciliazione)</option>
                      <option value="BONIFICO">Bonifico</option>
                      <option value="POS">POS</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">IBAN</label>
                    <input type="text" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono uppercase"
                           value={quickEditModal.fields.iban}
                           onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, iban: e.target.value.toUpperCase() } }))}
                           data-testid="qe-iban" />
                  </div>
                </div>
                <label className="text-xs font-medium text-slate-700 mt-3 block">📝 Note interne (non in fattura)</label>
                <textarea rows={2} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          value={quickEditModal.fields.note_interne}
                          onChange={e => setQuickEditModal(m => ({ ...m, fields: { ...m.fields, note_interne: e.target.value } }))}
                          data-testid="qe-note-interne" />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setQuickEditModal(null)} disabled={quickEditSubmitting}
                      className="px-4 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-50">
                Annulla
              </button>
              <button onClick={submitQuickEdit} disabled={quickEditSubmitting}
                      className="px-5 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50"
                      data-testid="qe-submit-btn">
                {quickEditSubmitting ? 'Salvataggio…' : 'Salva e rigenera PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Send Composer Modal (Sprint 5) — OTP + Cartaceo CTAs */}
      {unifiedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          // BUG FIX: usa mousedown+mouseup combo per evitare di chiudere il modal
          // quando l'utente seleziona testo (drag-to-select) e rilascia il mouse
          // sopra al backdrop. Solo un click "vero" sul backdrop chiude.
          onMouseDown={e => { e.currentTarget.dataset.bdDown = e.target === e.currentTarget ? '1' : '0'; }}
          onMouseUp={e => {
            const wasDown = e.currentTarget.dataset.bdDown === '1';
            e.currentTarget.dataset.bdDown = '0';
            if (wasDown && e.target === e.currentTarget && !unifiedSending) setUnifiedModal(null);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6"
               onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}
               data-testid="unified-compose-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {unifiedModal.testMode ? (
                  <>
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-1 rounded uppercase">
                      <Lightning size={12} weight="fill" /> TEST
                    </span>
                    <span>Invio di prova — {unifiedModal.group.ragione_sociale}</span>
                  </>
                ) : (
                  <>
                    <PaperPlaneRight size={20} weight="fill" className="text-orange-500" />
                    Invia al cliente — {unifiedModal.group.ragione_sociale}
                  </>
                )}
              </h2>
              <button onClick={() => setUnifiedModal(null)} disabled={unifiedSending}
                      className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                      data-testid="unified-close"><X size={20} /></button>
            </div>

            {unifiedModal.testMode && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3 mb-4 text-xs">
                <strong>Modalità TEST</strong> — l'email verrà inviata al destinatario alternativo qui sotto.
                I link OTP funzionano realmente per permetterti di verificare <strong>entrambe le opzioni di firma</strong> (OTP online + cartacea). Il cliente reale non riceverà nulla e il lead non verrà marcato come "inviato".
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
              <div className="text-xs text-slate-600 space-y-1">
                <div>
                  <span className="text-slate-500">Reply-To:</span>{' '}
                  <span className="font-mono">{unifiedModal.reply_to || '(commerciale)'}</span>
                  <span className="text-[10px] text-slate-400 ml-2">(da Impostazioni → Email Contratti)</span>
                </div>
                {unifiedModal.cc?.length > 0 && !unifiedModal.testMode && (
                  <div>
                    <span className="text-slate-500">CC automatico:</span>{' '}
                    <span className="font-mono">{unifiedModal.cc.join(', ')}</span>
                  </div>
                )}
              </div>
              {unifiedModal.attachments?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200 text-xs">
                  <span className="text-slate-500">📎 Allegati PDF ({unifiedModal.attachments.length}):</span>
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="unified-attachments-list">
                    {unifiedModal.attachments.map((a, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-white border rounded px-2 py-0.5 font-mono">
                        <Paperclip size={10} /> {a.filename}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Oggetto</label>
                <Input value={unifiedModal.subject}
                       onChange={e => setUnifiedModal(m => ({ ...m, subject: e.target.value }))}
                       className="text-sm" data-testid="unified-subject" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">
                    CTE (Condizioni Tecnico-Economiche)
                  </label>
                  <select value={unifiedModal.cteFilename}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === '__upload__') { customCteRef.current?.click(); return; }
                            setUnifiedModal(m => ({ ...m, cteFilename: v }));
                          }}
                          className="w-full text-sm border rounded-md px-3 py-1.5 bg-white"
                          data-testid="unified-cte-select">
                    <option value="">— Nessuna CTE allegata —</option>
                    {cteDocuments.map(d => (
                      <option key={d.id} value={d.filename}>{d.label}</option>
                    ))}
                    {unifiedModal.cteFilename?.startsWith('custom_cte/') && (
                      <option value={unifiedModal.cteFilename}>
                        📎 CTE Personalizzata — {unifiedModal.customCteName || 'PDF caricato'}
                      </option>
                    )}
                    <option value="__upload__">📎 Personalizzata (carica PDF)…</option>
                  </select>
                  <input ref={customCteRef} type="file" accept="application/pdf" className="hidden"
                         data-testid="unified-custom-cte-input"
                         onChange={async (e) => {
                           const f = e.target.files?.[0]; if (!f) return;
                           const fd = new FormData(); fd.append('file', f);
                           try {
                             const r = await fetch(`${API}/api/pre-contracts/upload-custom-cte`, {
                               method: 'POST', credentials: 'include', body: fd,
                             });
                             const d = await r.json();
                             if (r.ok) {
                               setUnifiedModal(m => ({ ...m, cteFilename: d.cte_filename, customCteName: d.display_name }));
                               toast.success(`CTE personalizzata caricata: ${d.display_name}`);
                             } else toast.error(d.detail || 'Upload CTE fallito');
                           } catch (_) { toast.error('Errore di rete'); }
                           e.target.value = '';
                         }} />
                  {unifiedModal.cteFilename?.startsWith('custom_cte/') && (
                    <div className="mt-1 text-[11px] text-orange-600 flex items-center gap-1" data-testid="unified-custom-cte-chip">
                      <Paperclip size={11} /> CTE fuori schema: sarà allegata, mostrata in firma e timbrata nel pacchetto firmato
                      <button className="text-red-400 hover:text-red-600 ml-1 font-bold"
                              title="Rimuovi CTE personalizzata"
                              onClick={() => setUnifiedModal(m => ({ ...m, cteFilename: '', customCteName: '' }))}>✕</button>
                    </div>
                  )}
                </div>
                <div className="text-xs space-y-1 pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={unifiedModal.attachCgf}
                           onChange={e => setUnifiedModal(m => ({ ...m, attachCgf: e.target.checked }))}
                           data-testid="unified-toggle-cgf" />
                    <span>📋 Allega <strong>Condizioni Generali</strong></span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={unifiedModal.attachPrivacy}
                           onChange={e => setUnifiedModal(m => ({ ...m, attachPrivacy: e.target.checked }))}
                           data-testid="unified-toggle-privacy" />
                    <span>🔒 Allega <strong>Informativa Privacy</strong></span>
                  </label>
                  {unifiedModal.hasLetteraComparativa && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={unifiedModal.attachLetteraComparativa}
                             onChange={e => setUnifiedModal(m => ({ ...m, attachLetteraComparativa: e.target.checked }))}
                             data-testid="unified-toggle-lettera" />
                      <span>📄 Allega <strong>Lettera Comparativa di risparmio</strong></span>
                    </label>
                  )}
                </div>
              </div>

              {/* Manual PDF upload */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">
                    📎 Allegati aggiuntivi (PDF)
                  </span>
                  <label className="text-xs font-medium text-orange-600 hover:text-orange-700 cursor-pointer flex items-center gap-1">
                    <Plus size={12} /> Aggiungi PDF
                    <input type="file" accept="application/pdf" className="hidden"
                           data-testid="unified-upload-pdf"
                           onChange={async (e) => {
                             const f = e.target.files?.[0]; if (!f) return;
                             const fd = new FormData(); fd.append('file', f);
                             try {
                               const r = await fetch(`${API}/api/pre-contracts/upload-attachment`, {
                                 method: 'POST', credentials: 'include', body: fd,
                               });
                               const d = await r.json();
                               if (r.ok) {
                                 setUnifiedModal(m => ({ ...m,
                                   extraAttachments: [...(m.extraAttachments || []), { id: d.id, filename: d.filename, size: d.size }],
                                   extraAttachmentIds: [...(m.extraAttachmentIds || []), d.id],
                                 }));
                                 toast.success(`${d.filename} allegato`);
                               } else {
                                 toast.error(d.detail || 'Upload fallito');
                               }
                             } catch (_) { toast.error('Errore di rete'); }
                             e.target.value = '';
                           }} />
                  </label>
                </div>
                {(unifiedModal.extraAttachments?.length || 0) === 0
                  ? <div className="text-xs text-slate-400">Nessun PDF aggiuntivo. Puoi caricare file ad hoc (max 10MB).</div>
                  : <div className="flex flex-wrap gap-1">
                      {unifiedModal.extraAttachments.map(x => (
                        <span key={x.id} className="inline-flex items-center gap-1 bg-white border rounded px-2 py-0.5 text-xs font-mono">
                          <Paperclip size={10} /> {x.filename}
                          <button className="text-red-400 hover:text-red-600 ml-1"
                                  onClick={() => setUnifiedModal(m => ({
                                    ...m,
                                    extraAttachments: m.extraAttachments.filter(a => a.id !== x.id),
                                    extraAttachmentIds: m.extraAttachmentIds.filter(i => i !== x.id),
                                  }))}>✕</button>
                        </span>
                      ))}
                    </div>
                }
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Testo del messaggio
                  <span className="text-slate-400 font-normal ml-2">
                    (solo testo — i 2 box di firma e la firma del team vengono aggiunti automaticamente)
                  </span>
                </label>
                <textarea value={unifiedModal.introText}
                          onChange={e => setUnifiedModal(m => ({ ...m, introText: e.target.value }))}
                          rows={6}
                          placeholder="Scrivi qui il testo introduttivo che vuoi far leggere al cliente..."
                          className="w-full text-sm border rounded-md px-3 py-2 leading-relaxed"
                          data-testid="unified-intro-text" />
                <div className="text-[10px] text-slate-400 mt-1">
                  Una riga vuota = nuovo paragrafo. Il testo è automaticamente protetto contro code injection.
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-slate-600 mb-1 flex items-center justify-between">
                  <span>Anteprima email completa</span>
                  {unifiedPreviewLoading && <span className="text-[10px] text-slate-400">aggiornamento...</span>}
                </div>
                <div className="border rounded-lg p-4 bg-white max-h-72 overflow-y-auto"
                     data-testid="unified-preview"
                     dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(unifiedModal.body, { ADD_ATTR: ['target'] }) }} />
              </div>
            </div>

            {/* ===== Destinatario + CC + Firmatario in basso ===== */}
            <div className="mt-5 pt-4 border-t-2 border-orange-200 bg-orange-50/50 -mx-6 -mb-6 px-6 pb-6 rounded-b-xl">
              {/* Sprint 5.5 — Firmatario decoupled (cliente che firma legalmente) */}
              {!unifiedModal.testMode && (
                <div className="mb-3 pb-3 border-b border-orange-200/60">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                    🖋 Firmatario (cliente che firma legalmente)
                    <span className="text-[10px] font-normal text-slate-500 normal-case">— riceve OTP via email o SMS</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1">Email firmatario (per OTP via email)</label>
                      <Input value={unifiedModal.signerEmail || ''}
                             onChange={e => setUnifiedModal(m => ({ ...m, signerEmail: e.target.value }))}
                             placeholder="email@cliente.it"
                             className="text-sm font-mono" data-testid="unified-signer-email" />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-1">Telefono firmatario (per OTP via SMS)</label>
                      <Input value={unifiedModal.signerPhone || ''}
                             onChange={e => setUnifiedModal(m => ({ ...m, signerPhone: e.target.value }))}
                             placeholder="+39 333 1234567"
                             className="text-sm font-mono" data-testid="unified-signer-phone" />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-bold text-orange-900 uppercase tracking-wide block mb-1">
                    {unifiedModal.testMode ? '📧 Destinatario TEST' : '📧 Destinatario contratto'}
                  </label>
                  <Input value={unifiedModal.to_email}
                         onChange={e => setUnifiedModal(m => ({ ...m, to_email: e.target.value }))}
                         placeholder={unifiedModal.testMode ? 'es. tuaemail@tuodominio.it' : 'email cliente o intermediario'}
                         className="text-sm font-mono" data-testid="unified-to-email" />
                  {!unifiedModal.testMode && unifiedModal.to_email && unifiedModal.to_email !== unifiedModal.signerEmail && (
                    <div className="text-[10px] text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded">
                      ⓘ Destinatario diverso dal firmatario: contratto va a <strong>{unifiedModal.to_email}</strong>, OTP va al firmatario.
                    </div>
                  )}
                  {unifiedModal.testMode && unifiedModal.test_email_default && (
                    <button type="button"
                            className="text-[10px] text-orange-600 hover:text-orange-700 mt-1"
                            onClick={() => setUnifiedModal(m => ({ ...m, to_email: m.test_email_default }))}
                            data-testid="unified-use-test-default">
                      Usa email di Test predefinita: {unifiedModal.test_email_default}
                    </button>
                  )}
                </div>
                <div>
                  <label className="text-xs font-bold text-orange-900 uppercase tracking-wide block mb-1">
                    📨 CC (Copia Conoscenza) — opzionale
                  </label>
                  <Input value={unifiedModal.ccExtra || ''}
                         onChange={e => setUnifiedModal(m => ({ ...m, ccExtra: e.target.value }))}
                         placeholder="collega@azienda.it"
                         className="text-sm font-mono" data-testid="unified-cc-extra" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500 flex items-center gap-3">
                  <span>
                    {unifiedModal.testMode
                      ? 'L\'invio TEST non aggiorna lo stato del cliente.'
                      : 'L\'invio definitivo aggiorna lo stato del lead e fa scattare i reminder automatici.'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setKycModal({
                      leadId: unifiedModal.leadId,
                      leadName: unifiedModal.group?.ragione_sociale,
                      onAfter: null,
                      allowAdminSkip: isAdmin,
                      onSkip: () => { setKycModal(null); submitUnifiedSend(true); },
                    })}
                    className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1"
                    data-testid="unified-open-kyc-btn">
                    <ShieldCheck size={12} weight="fill" /> Carica KYC
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setUnifiedModal(null)} disabled={unifiedSending}
                          data-testid="unified-cancel">Annulla</Button>
                  {unifiedModal.testMode ? (
                    <Button onClick={() => submitUnifiedSend()} disabled={unifiedSending || !unifiedModal.to_email}
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            data-testid="unified-send-test-btn">
                      <Lightning size={14} className="mr-1" weight="fill" />
                      {unifiedSending ? 'Invio TEST...' : 'Invia TEST'}
                    </Button>
                  ) : (
                    <Button onClick={() => setUnifiedConfirm(true)}
                            disabled={unifiedSending || !unifiedModal.to_email}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="unified-send-definitive-btn">
                      <PaperPlaneRight size={14} className="mr-1" weight="fill" />
                      Invia al cliente
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog before definitive send */}
      {unifiedConfirm && unifiedModal && !unifiedModal.testMode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
             onClick={() => !unifiedSending && setUnifiedConfirm(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
               onClick={e => e.stopPropagation()} data-testid="unified-confirm-dialog">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-full">
                <Warning size={22} weight="fill" className="text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Invio definitivo al cliente</h3>
                <p className="text-sm text-slate-600">
                  Stai per inviare la mail <strong>definitiva</strong> a{' '}
                  <strong className="font-mono text-orange-700">{unifiedModal.to_email}</strong>
                  {unifiedModal.cc?.length > 0 && <> (CC <strong className="font-mono">{unifiedModal.cc[0]}</strong>)</>}.
                  <br /><br />
                  Contiene <strong>entrambe le opzioni di firma</strong> (OTP online + cartacea)
                  e {unifiedModal.attachments?.length || 0} PDF allegati.
                  <br /><br />
                  Dopo l'invio, se il cliente non apre entro 48h partirà il reminder automatico.
                  <br /><br />
                  <strong>Confermi?</strong>
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUnifiedConfirm(false)}
                      disabled={unifiedSending} data-testid="unified-confirm-cancel">
                Annulla
              </Button>
              <Button onClick={() => submitUnifiedSend()} disabled={unifiedSending}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="unified-confirm-send">
                <PaperPlaneRight size={14} className="mr-1" weight="fill" />
                {unifiedSending ? 'Invio in corso...' : 'Sì, invia definitiva'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Compose Modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(() => setEmailModal(null))}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="email-compose-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">
                {emailModal.batch ? `Invio Massivo - ${emailModal.count} clienti` : `Invia Pre-contratto - ${emailModal.ragione_sociale}`}
              </h2>
              <button onClick={() => setEmailModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>

            {!emailModal.batch && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Destinatario:</span> <strong>{emailModal.email || '(email mancante)'}</strong></div>
                  <div><span className="text-slate-500">POD:</span> {emailModal.is_multisito ? `${emailModal.pod_count} siti (Multisito)` : emailModal.pods?.[0]}</div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Oggetto</label>
                <Input value={emailForm.subject} onChange={e => setEmailForm(p => ({ ...p, subject: e.target.value }))}
                  className="text-sm" data-testid="email-subject" />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Corpo Email (HTML)</label>
                <textarea value={emailForm.body} onChange={e => setEmailForm(p => ({ ...p, body: e.target.value }))}
                  rows={10} className="w-full text-sm border rounded-md px-3 py-2 font-mono text-xs"
                  data-testid="email-body" />
              </div>

              {/* Anteprima */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Anteprima</label>
                <div className="border rounded-md p-4 bg-white max-h-48 overflow-y-auto text-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailForm.body) }} />
              </div>

              {/* Allegati */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-2">
                  <Paperclip size={14} className="inline mr-1" /> Allegati
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="text-slate-700">Contratto pre-compilato (automatico)</span>
                  </div>
                  {!emailModal.batch && emailModal?.is_multisito && (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle size={14} className="text-purple-600" />
                      <span className="text-slate-700">Allegato Multisito (automatico)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-600">CTE:</span>
                    <select value={emailForm.cte} onChange={e => setEmailForm(p => ({ ...p, cte: e.target.value }))}
                      className="text-xs border rounded px-2 py-1 bg-white" data-testid="email-cte-select">
                      <option value="">-- Nessuna CTE --</option>
                      {cteDocuments.map(d => <option key={d.id} value={d.filename}>{d.label}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={emailForm.condizioni} onChange={e => setEmailForm(p => ({ ...p, condizioni: e.target.checked }))}
                      className="accent-orange-500" />
                    Condizioni Generali di fornitura
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={emailForm.privacy} onChange={e => setEmailForm(p => ({ ...p, privacy: e.target.checked }))}
                      className="accent-orange-500" />
                    Informativa Privacy
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t gap-3">
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={emailForm.testEmail || ''}
                  onChange={e => setEmailForm(p => ({ ...p, testEmail: e.target.value }))}
                  placeholder="Email di test (es. tuaemail@azienda.it)"
                  className="text-xs h-8 max-w-[260px]"
                  data-testid="test-email-input"
                />
                <button
                  onClick={async () => {
                    if (!emailForm.testEmail?.trim()) { toast.error('Inserisci un indirizzo email di test'); return; }
                    setSending(true);
                    try {
                      const res = await fetch(`${API}/api/pre-contracts/send-test-email`, {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          lead_id: emailModal.batch ? null : emailModal.id,
                          test_email: emailForm.testEmail.trim(),
                          subject: emailForm.subject, body: emailForm.body,
                          attach_cte: emailForm.cte, attach_condizioni: emailForm.condizioni, attach_privacy: emailForm.privacy,
                        })
                      });
                      const data = await res.json();
                      if (res.ok) toast.success(data.message);
                      else toast.error(data.detail);
                    } catch (e) { toast.error('Errore invio test'); }
                    finally { setSending(false); }
                  }}
                  disabled={sending || !emailForm.testEmail?.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-md font-medium disabled:opacity-50 whitespace-nowrap"
                  data-testid="send-test-email-btn"
                >
                  <EnvelopeSimple size={14} />
                  {sending ? 'Invio...' : 'Invia Test'}
                </button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEmailModal(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Annulla</button>
                <button onClick={sendEmail} disabled={sending || (!emailModal.batch && !emailModal.email)}
                  className="flex items-center gap-2 px-6 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium disabled:opacity-50"
                  data-testid="send-email-btn">
                  <PaperPlaneRight size={16} />
                  {sending ? 'Invio in corso...' : emailModal.batch ? `Invia a ${emailModal.count} clienti` : 'Invia Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" {...backdropClick(() => setPreviewHtml(null))}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col" {...stopBackdropPropagation} data-testid="preview-modal-pc">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-bold text-slate-900">Anteprima Contratto</h2>
              <button onClick={() => setPreviewHtml(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <iframe srcDoc={previewHtml} className="flex-1 w-full" title="Anteprima Contratto" />
          </div>
        </div>
      )}

      {/* Email History Modal */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(() => setHistoryModal(null))}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="email-history-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Storico Email - {historyModal}</h2>
              <div className="flex items-center gap-2">
                <a href={`/marketing/communications?ragione=${encodeURIComponent(historyModal)}`}
                   className="text-xs text-orange-600 hover:underline"
                   data-testid="open-marketing-comms">
                  Apri in Marketing Comunicazioni →
                </a>
                <button onClick={() => setHistoryModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>
            </div>
            {emailHistory.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nessun invio registrato</p>
            ) : (
              <div className="space-y-3">
                {emailHistory.map(email => (
                  <div key={email.id} className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-900">{email.subject}</span>
                      {email.opened ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                          <Eye size={10} /> Aperto {email.opened_at ? `il ${new Date(email.opened_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-50 text-slate-500 text-[10px] font-medium">
                          <EnvelopeSimple size={10} /> Non aperto
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      Inviato a <strong>{email.to_email}</strong> il {new Date(email.sent_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {email.sent_by && ` da ${email.sent_by}`}
                    </div>
                    {email.attachments?.length > 0 && (
                      <div className="mt-1 text-[10px] text-slate-400">
                        <Paperclip size={10} className="inline mr-1" />
                        {email.attachments.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sprint 4: Validation Gate Dialog */}
      <ValidationGateDialog
        open={!!validationGateLead}
        onOpenChange={(o) => { if (!o) setValidationGateLead(null); }}
        leadId={validationGateLead?.leadId}
        leadName={validationGateLead?.leadName}
        onConfirmSend={() => {
          const proceed = validationGateLead?.onProceed;
          setValidationGateLead(null);
          if (proceed) proceed();
        }}
        sendLabel="Procedi all'invio"
      />

      {/* Sprint 46 Task 2: KYC gate vincolante */}
      <KycUploadModal
        open={!!kycModal}
        leadId={kycModal?.leadId}
        leadName={kycModal?.leadName}
        allowAdminSkip={!!kycModal?.allowAdminSkip}
        onSkip={kycModal?.onSkip}
        onClose={() => setKycModal(null)}
        onSuccess={() => {
          const after = kycModal?.onAfter;
          setKycModal(null);
          if (after) setTimeout(after, 200);
        }}
      />
    </div>
  );
}
